import { ReactNode, useState } from 'react';

import { normalizeToE164 } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getCountryForTimezone } from 'countries-and-timezones';
import { customList } from 'country-codes-list';
import ReCAPTCHA from 'react-google-recaptcha';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { match } from 'ts-pattern';
import { isMobilePhone } from 'validator';
import { z } from 'zod';

import sitemateLogo from '@/assets/sitemate.svg';
import ArrowLeftIcon from '@/components/icons/arrow-left';
import CloseIcon from '@/components/icons/close';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { env } from '@/environment';
import { getAWAUserQueryOptions } from '@/queries/awa';
import { AWAGlobalApiService } from '@/services/global-api/awa-global-api.service';
import { useAWAStore } from '@/stores/awa.store';
import { checkIfUserProfileComplete } from '@/utils/awa';

const deliveryMethodSchema = z.enum(['sms', 'call']);
type DeliveryMethod = z.infer<typeof deliveryMethodSchema>;

enum LoginStep {
  GET_CODE = 'GET_CODE',
  VERIFY_CODE = 'VERIFY_CODE',
}

function validatePhoneNumber(phoneNumber: string) {
  return isMobilePhone(normalizeToE164(phoneNumber) ?? phoneNumber);
}

const rootFormSchema = z.object({
  step: z.nativeEnum(LoginStep),
  twoFactorId: z.string().optional(),
  phoneNumber: z.string().refine(validatePhoneNumber),
  code: z.string().min(6),
  deliveryMethod: deliveryMethodSchema,
});

export function AWAAuthLoginPage() {
  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get('workspaceId');
  const redirectTo = searchParams.get('redirectTo');
  const { authTokens } = useAWAStore();

  if (!workspaceId || workspaceId === 'undefined' || workspaceId === 'null') {
    return (
      <div className="flex h-[100dvh] flex-col items-center justify-center">
        <div className="text-2xl font-medium">Invalid URL</div>
      </div>
    );
  }

  if (authTokens?.accessToken) {
    if (redirectTo === workspaceId) {
      return <Navigate to={`/${workspaceId}`} />;
    }
    return <Navigate to={`/${workspaceId}/${redirectTo}`} />;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={env.recaptcha.smmV3SiteKey}>
      <Page />
    </GoogleReCaptchaProvider>
  );
}

function Page() {
  const form = useForm<z.infer<typeof rootFormSchema>>({
    resolver: zodResolver(rootFormSchema),
    defaultValues: {
      step: LoginStep.GET_CODE,
      phoneNumber: defaultCountryCode,
      code: '',
      twoFactorId: undefined,
      deliveryMethod: 'sms',
    },
  });
  const { watch } = form;

  const currentStep = watch('step');

  function resetForm() {
    form.reset();
  }

  return (
    <FormProvider {...form}>
      <div className="text-default-text relative flex h-screen flex-col gap-4 px-6 py-25">
        {currentStep === LoginStep.VERIFY_CODE && (
          <div className="absolute top-4 left-4">
            <Button variant="ghost" size="icon" onClick={resetForm}>
              <ArrowLeftIcon className="size-6" />
            </Button>
          </div>
        )}

        <div className="py-6">
          <img src={sitemateLogo} alt="Sitemate Logo" className="h-[60px]" />
        </div>
        {match(currentStep)
          .with(LoginStep.GET_CODE, () => <GetCodeStep />)
          .with(LoginStep.VERIFY_CODE, () => <VerifyCodeStep />)
          .exhaustive()}
      </div>
    </FormProvider>
  );
}

const getCodeFormSchema = z.object({
  phoneNumber: z.string().refine(validatePhoneNumber),
});

function GetCodeStep() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [useReCAPTCHAV2, setUseReCAPTCHAV2] = useState(false);
  const [reCAPTCHAV2Token, setReCAPTCHAV2Token] = useState<string | null>(null);

  const rootForm = useFormContext<z.infer<typeof rootFormSchema>>();
  const { watch: watchRootForm } = rootForm;

  const currentStep = watchRootForm('step');

  const { mutateAsync: getOTP } = useMutation({
    mutationFn: (params: Parameters<typeof AWAGlobalApiService.getOTP>[0]) => AWAGlobalApiService.getOTP(params),
  });

  const form = useForm<z.infer<typeof getCodeFormSchema>>({
    resolver: zodResolver(getCodeFormSchema),
    defaultValues: {
      phoneNumber: defaultCountryCode,
    },
  });

  function onOTPSent(twoFactorId: string) {
    rootForm.setValue('phoneNumber', form.getValues().phoneNumber);
    rootForm.setValue('twoFactorId', twoFactorId);
    rootForm.setValue('deliveryMethod', 'sms');
    rootForm.setValue('step', LoginStep.VERIFY_CODE);
  }

  async function triggerRecaptchaV3(recaptchaToken: string) {
    try {
      const res = await getOTP({
        phoneNumber: form.getValues().phoneNumber,
        deliveryMethod: 'sms',
        recaptchaToken,
        isFallback: false,
      });

      if (res.status === 'invalid-captcha') {
        setUseReCAPTCHAV2(true);
      } else if (res.status === 'valid-captcha') {
        onOTPSent(res.twoFactorId);
      }
    } catch {
      setUseReCAPTCHAV2(true);
    }
  }

  async function triggerRecaptchaV2(recaptchaToken: string) {
    const res = await getOTP({
      phoneNumber: form.getValues().phoneNumber,
      deliveryMethod: 'sms',
      recaptchaToken,
      isFallback: true,
    });

    if (res.status === 'valid-captcha') {
      onOTPSent(res.twoFactorId);
    }
  }

  async function requestOTP() {
    if (!useReCAPTCHAV2 && executeRecaptcha) {
      const reCAPTCHAV3Token = await executeRecaptcha('signup');
      await triggerRecaptchaV3(reCAPTCHAV3Token);
    } else if (useReCAPTCHAV2 && reCAPTCHAV2Token) {
      await triggerRecaptchaV2(reCAPTCHAV2Token);
    }
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(() => requestOTP())}>
      <h1 className="text-2xl font-bold">Enter your phone number</h1>

      <p>We'll send you a code to login, please enter your phone number. Your phone number will not be shared or publicly visible to anyone else.</p>

      <Controller
        control={form.control}
        name="phoneNumber"
        render={({ field }) => (
          <PhoneInput
            value={field.value}
            disabled={currentStep !== LoginStep.GET_CODE}
            placeholder="Enter your phone number"
            className="text-base"
            userCountry={(() => {
              try {
                const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const country = getCountryForTimezone(timezone);
                return country?.id.toLowerCase() || 'au';
              } catch {
                return 'au';
              }
            })()}
            error={!!form.formState.errors.phoneNumber}
            onChange={field.onChange}
            countrySelectTriggerClassName="text-base"
            inputClassName="text-base"
          />
        )}
      />

      {useReCAPTCHAV2 && <ReCAPTCHA sitekey={env.recaptcha.smmV2SiteKey} onChange={setReCAPTCHAV2Token} size="normal" />}

      <Button className="text-base" loading={form.formState.isSubmitting} disabled={!form.formState.isValid}>
        Get code
      </Button>
    </form>
  );
}

const verifyCodeFormSchema = z.object({
  code: z.string().min(6),
});

function VerifyCodeStep() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuthTokens } = useAWAStore();

  const rawRedirect = searchParams.get('rawRedirect');
  const workspaceId = searchParams.get('workspaceId');
  const redirectTo = searchParams.get('redirectTo');

  const rootForm = useFormContext<z.infer<typeof rootFormSchema>>();
  const { watch: watchRootForm } = rootForm;

  const [useReCAPTCHAV2, setUseReCAPTCHAV2] = useState(false);
  const [reCAPTCHAV2Token, setReCAPTCHAV2Token] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [isRequestingCall, setIsRequestingCall] = useState(false);
  const [isInvalidCodeDialogOpen, setIsInvalidCodeDialogOpen] = useState(false);
  const [isResendDialogOpen, setIsResendDialogOpen] = useState(false);

  const currentStep = watchRootForm('step');
  const phone = watchRootForm('phoneNumber');
  const deliveryMethod = watchRootForm('deliveryMethod');

  const { mutateAsync: verifyOTP } = useMutation({
    mutationFn: (params: Parameters<typeof AWAGlobalApiService.verifyOTP>[0]) => AWAGlobalApiService.verifyOTP(params),
    onError: () => {
      setIsInvalidCodeDialogOpen(true);
    },
  });

  const { mutateAsync: getOTP } = useMutation({
    mutationFn: (params: Parameters<typeof AWAGlobalApiService.getOTP>[0]) => AWAGlobalApiService.getOTP(params),
  });

  async function triggerRecaptchaV3(recaptchaToken: string, method: DeliveryMethod) {
    try {
      const res = await getOTP({
        phoneNumber: normalizeToE164(rootForm.getValues().phoneNumber) ?? rootForm.getValues().phoneNumber,
        deliveryMethod: method,
        recaptchaToken,
        isFallback: false,
      });

      if (res.status === 'invalid-captcha') {
        setUseReCAPTCHAV2(true);
      } else if (res.status === 'valid-captcha') {
        rootForm.setValue('twoFactorId', res.twoFactorId);
        rootForm.setValue('deliveryMethod', method);
        setIsResendDialogOpen(true);
      }
    } catch {
      setUseReCAPTCHAV2(true);
    }
  }

  async function triggerRecaptchaV2(recaptchaToken: string, method: DeliveryMethod) {
    const res = await getOTP({
      phoneNumber: normalizeToE164(rootForm.getValues().phoneNumber) ?? rootForm.getValues().phoneNumber,
      deliveryMethod: method,
      recaptchaToken,
      isFallback: true,
    });

    if (res.status === 'valid-captcha') {
      rootForm.setValue('twoFactorId', res.twoFactorId);
      rootForm.setValue('deliveryMethod', method);
      setIsResendDialogOpen(true);
    }
  }

  const form = useForm<z.infer<typeof verifyCodeFormSchema>>({
    resolver: zodResolver(verifyCodeFormSchema),
    defaultValues: {
      code: '',
    },
  });

  async function requestOTP(method: DeliveryMethod) {
    if (!useReCAPTCHAV2 && executeRecaptcha) {
      const reCAPTCHAV3Token = await executeRecaptcha('signup');
      await triggerRecaptchaV3(reCAPTCHAV3Token, method);
    } else if (useReCAPTCHAV2 && reCAPTCHAV2Token) {
      await triggerRecaptchaV2(reCAPTCHAV2Token, method);
    }
  }

  async function handleResend() {
    setIsResending(true);
    try {
      await requestOTP(deliveryMethod);
    } finally {
      setIsResending(false);
    }
  }

  async function handleRequestCall() {
    setIsRequestingCall(true);
    try {
      await requestOTP('call');
    } finally {
      setIsRequestingCall(false);
    }
  }

  const { refetch: fetchUser } = useQuery({
    ...getAWAUserQueryOptions(),
    enabled: false,
  });

  async function handleSubmit(data: z.infer<typeof verifyCodeFormSchema>) {
    const twoFactorId = watchRootForm('twoFactorId');

    if (!twoFactorId) {
      throw new Error('Two factor ID is required');
    }

    const res = await verifyOTP({ code: data.code, twoFactorId });

    if (res) {
      setAuthTokens({
        accessToken: res.accessToken,
        accessTokenExpiration: res.accessTokenExpiration,
        refreshToken: res.refreshToken,
        refreshTokenId: res.refreshTokenId,
      });

      const user = await fetchUser().then((res) => res.data);

      if (user) {
        const isUserProfileComplete = checkIfUserProfileComplete(user);

        if (!isUserProfileComplete) {
          navigate(`/auth/onboarding?${searchParams.toString()}`);
        } else {
          if (rawRedirect) {
            navigate(`/${redirectTo}`);
          } else {
            const targetPath = redirectTo === workspaceId ? '' : redirectTo;
            navigate(`/${workspaceId}/${targetPath}`);
          }
        }
      }
    }
  }

  const codePromptCopy =
    deliveryMethod === 'call' ? `We're calling ${phone} with your verification code. Enter it below.` : `We've sent an activation code to ${phone}.`;

  const resendDialogDescription =
    deliveryMethod === 'call'
      ? `We're calling ${phone} again with a new code. Make sure you're using the latest code you receive (previous codes will be invalid).`
      : `A new code has been sent to ${phone}. Make sure you're using the latest code you receive (previous codes will be invalid).`;

  return (
    <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
      <h1 className="text-2xl font-bold">Enter your code</h1>

      <p>{codePromptCopy}</p>

      <Controller
        control={form.control}
        name="code"
        render={({ field }) => (
          <Input
            value={field.value}
            disabled={currentStep !== LoginStep.VERIFY_CODE}
            placeholder="XXXXXX"
            className="text-base"
            maxLength={6}
            pattern="[0-9]*"
            inputMode="numeric"
            error={!!form.formState.errors.code}
            onChange={field.onChange}
          />
        )}
      />

      <Button className="text-base" loading={form.formState.isSubmitting} disabled={!form.formState.isValid}>
        Submit
      </Button>

      {useReCAPTCHAV2 && <ReCAPTCHA sitekey={env.recaptcha.smmV2SiteKey} onChange={setReCAPTCHAV2Token} size="normal" />}

      <p className="text-secondary-text">If you don't receive your code within a minute, tap Resend to try again or Request a call to get the code by phone.</p>

      <div className="flex gap-6">
        <button
          className="text-default-blue py-2 underline disabled:opacity-50"
          onClick={handleResend}
          type="button"
          disabled={form.formState.isSubmitting || isResending || isRequestingCall}>
          {isResending ? 'Resending…' : 'Resend'}
        </button>
        <button
          className="text-default-blue py-2 underline disabled:opacity-50"
          onClick={handleRequestCall}
          type="button"
          disabled={form.formState.isSubmitting || isResending || isRequestingCall || (useReCAPTCHAV2 && !reCAPTCHAV2Token)}>
          {isRequestingCall ? 'Calling you…' : 'Request a call'}
        </button>
      </div>

      <AuthDialog
        open={isResendDialogOpen}
        onOpenChange={setIsResendDialogOpen}
        title={deliveryMethod === 'call' ? 'Calling you with a new code' : 'Resent code'}
        description={resendDialogDescription}
      />
      <AuthDialog
        open={isInvalidCodeDialogOpen}
        onOpenChange={setIsInvalidCodeDialogOpen}
        title="Invalid code"
        description="The entered code did not match. Tap Resend to get a new code, then try again."
      />
    </form>
  );
}

function AuthDialog({
  open,
  onOpenChange,
  title,
  description,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex w-[90%] flex-col gap-[10px]">
        {/* Visually hidden for a11y */}
        <VisuallyHidden>
          <DialogTitle>{title}</DialogTitle>
        </VisuallyHidden>

        <DialogClose className="focus:ring-default-blue data-[state=open]:bg-default-blue/10 data-[state=open]:text-default-text absolute top-4 right-4 size-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none">
          <CloseIcon className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>

        <h1 className="text-center text-xl font-medium">{title}</h1>

        <DialogDescription className="text-center text-base">{description}</DialogDescription>

        <Button className="mx-auto mt-2 w-fit px-6" onClick={() => onOpenChange(false)}>
          OK
        </Button>
      </DialogContent>
    </Dialog>
  );
}

const defaultCountryCode = (() => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const country = getCountryForTimezone(timezone);
    if (country) {
      const countryPhoneCodes = customList('countryCode', '{countryCode}');
      return countryPhoneCodes[country.id] ? `+${countryPhoneCodes[country.id]}` : '+61';
    }
    return '+61';
  } catch {
    return '+61';
  }
})();
